import React, { useState, useEffect } from 'react';

const BreederRequestModal = ({ 
  show, 
  onClose, 
  handler, 
  breeder, 
  onSubmit, 
  maxBirds = 5,
  approvedBirdsWithHandler = 0,
  remainingBirdsOverall = null,
  isRotationMode = false,
  rotationBirdCount = 0,
  existingBirdDetails = null
}) => {
  // In rotation mode, numberOfBirds is locked to rotationBirdCount
  const [numberOfBirds, setNumberOfBirds] = useState(() => {
    return isRotationMode ? rotationBirdCount : 1;
  });
  const [submitting, setSubmitting] = useState(false);
  const [requestNote, setRequestNote] = useState('');
  const [showConfirmation, setShowConfirmation] = useState(false);

   const [birdDetails, setBirdDetails] = useState([
     { bandNumber: '', color: '', sex: '' }
   ]);

   // Initialize numberOfBirds and birdDetails based on rotation mode
   useEffect(() => {
     if (isRotationMode) {
       setNumberOfBirds(rotationBirdCount);
       
       // Pre-populate from existing bird details if provided
       if (existingBirdDetails && existingBirdDetails.length > 0) {
         setBirdDetails(
           existingBirdDetails.map(bird => ({
             bandNumber: bird.bandNumber || '',
             color: bird.color || '',
             sex: bird.sex || ''
           }))
         );
       } else {
         // Create empty placeholders based on rotationBirdCount
         setBirdDetails(
           Array.from({ length: rotationBirdCount }, () => ({
             bandNumber: '',
             color: '',
             sex: ''
           }))
         );
       }
     }
   }, [isRotationMode, rotationBirdCount, existingBirdDetails]);

   // Update birdDetails array when numberOfBirds changes (only in non-rotation mode)
   useEffect(() => {
     if (!isRotationMode) {
       const newBirdDetails = [...Array(numberOfBirds)].map((_, idx) => 
         birdDetails[idx] || { bandNumber: '', color: '', sex: '' }
       );
       setBirdDetails(newBirdDetails);
     }
   }, [numberOfBirds, isRotationMode]);

  if (!show) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Show confirmation for handlers requiring calls (skip for rotation mode)
    if (!isRotationMode && handler && handler.handlerInfo && handler.handlerInfo.preferCallFirst === true) {
      setShowConfirmation(true);
      return;
    }
    await submitRequest();
  };

  const submitRequest = async () => {
    setSubmitting(true);
    
    try {
       // In rotation mode, send bird details as-is; in regular mode, keep old structure for backward compatibility
       const birdsToSend = isRotationMode 
         ? birdDetails.map(bird => ({
             bandNumber: bird.bandNumber || '',
             color: bird.color || '',
             sex: bird.sex || ''
           }))
         : birdDetails.map(bird => ({
             bandNumber: getBandNumber(bird),
             org: bird.org,
             serialNumber: bird.serialNumber,
             clubCode: bird.clubCode,
             year: bird.year,
             color: bird.color || '[To be added]',
             sex: bird.sex || '[To be added]',
             status: 'placeholder'
           }));

       await onSubmit({
         handlerId: handler.id,
         breederId: breeder.id,
         numberOfBirds: numberOfBirds,
         birdDetails: birdsToSend,
         requestNote: requestNote,
         status: 'pending'
       });
       
       // Reset form for next use
       setNumberOfBirds(1);
       setRequestNote('');

       setBirdDetails([{ bandNumber: '', color: '', sex: '' }]);
      // Close modal after parent completes
      onClose();
    } catch (error) {
      // Close modal even on error
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmSubmit = async () => {
    setShowConfirmation(false);
    await submitRequest();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
    handleClose(); // Close the entire modal to find another handler
  };

  const handleClose = () => {
    setShowConfirmation(false);
    onClose();
  };

  const handleIncrement = () => {
    if (numberOfBirds < maxBirds) {
      setNumberOfBirds(numberOfBirds + 1);
    }
  };

  const handleDecrement = () => {
    if (numberOfBirds > 1) {
      setNumberOfBirds(numberOfBirds - 1);
    }
  };

  const handleBirdDetailChange = (index, field, value) => {
    const newBirdDetails = [...birdDetails];
    
    // Validate serial number (max 15 digits)
    if (field === 'serialNumber') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 15) {
        newBirdDetails[index][field] = numericValue;
      }
    }
    // Validate year (max 2 digits)
    else if (field === 'year') {
      const numericValue = value.replace(/\D/g, '');
      if (numericValue.length <= 2) {
        newBirdDetails[index][field] = numericValue;
      }
    }
    // Other fields - no validation
    else {
      newBirdDetails[index][field] = value;
    }
    
    setBirdDetails(newBirdDetails);
  };

  const getBandNumber = (bird) => {
    if (!bird.org && !bird.serialNumber && !bird.clubCode && !bird.year) {
      return '[To be added]';
    }
    return `${bird.org || '?'}-${bird.serialNumber || '?'}-${bird.clubCode || '?'}-${bird.year || '?'}`;
  };

  return (
    <div 
      className="modal fade show" 
      style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={handleClose}
    >
      <div 
        className="modal-dialog modal-dialog-centered"
        style={{
          maxWidth: '600px',
          width: '100%',
          margin: '0.5rem',
          height: 'auto',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-content" style={{ minHeight: '200px', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-header" style={{ borderBottom: '3px solid #0b3d91' }}>
            <h5 className="modal-title" style={{ color: '#0b3d91' }}>
              <i className="bi bi-send me-2"></i>
              Request Handler Assignment
            </h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={handleClose}
              disabled={submitting}
            ></button>
          </div>
          
          <form onSubmit={handleSubmit}>
            <div className="modal-body" style={{ flex: '1 1 auto', overflowY: 'auto', maxHeight: '70vh', padding: '1.5rem' }}>
              {/* Handler Information Section */}
              <div className="mb-4">
                <h6 className="fw-bold mb-2">Handler Information</h6>
                <div className="p-3 bg-light rounded">
                  {isRotationMode ? (
                    <>
                      <div><strong>Admin Rotation (System Placement)</strong></div>
                      <div className="text-muted small">Your birds will be assigned by the race administrator</div>
                    </>
                  ) : (
                    <>
                      <div><strong>{handler.fullName}</strong></div>
                      <div className="text-muted small">{handler.loftName}</div>
                      <div className="text-muted small mt-1">
                        <div>Section: {handler.handlerInfo?.section || 'N/A'}</div>
                        <div>Distance: {handler.handlerInfo?.distanceFromRaceStation ? `${handler.handlerInfo.distanceFromRaceStation} miles` : 'N/A'}</div>
                      </div>
                    </>
                  )}
                </div>
              </div>

              {!isRotationMode && handler && handler.handlerInfo && handler.handlerInfo.preferCallFirst === true && (
                <div className="alert alert-warning mb-4">
                  <i className="bi bi-telephone-fill me-2"></i>
                  <strong>Call Required First:</strong> This handler requires you to call them at{' '}
                  <strong>{handler.mobile}</strong> to reserve a slot before submitting this request.
                  Please call them first to confirm availability.
                </div>
              )}


               <div className="mb-3">
                <label htmlFor="numberOfBirds" className="form-label fw-bold">
                  Number of Birds <span className="text-danger">*</span>
                </label>
                {isRotationMode ? (
                  <div className="p-3 bg-light rounded">
                    <div style={{ fontSize: '2.5rem', fontWeight: 'bold', textAlign: 'center', color: '#0b3d91' }}>
                      {numberOfBirds}
                    </div>
                    <small className="text-muted d-block text-center mt-2">
                      {numberOfBirds} bird{numberOfBirds !== 1 ? 's' : ''} required for rotation (locked)
                    </small>
                  </div>
                ) : (
                  <div className="d-flex align-items-center gap-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-lg"
                      onClick={handleDecrement}
                      disabled={numberOfBirds <= 1 || submitting}
                      style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}
                    >
                      −
                    </button>
                    <div 
                      className="text-center fw-bold"
                      style={{ fontSize: '2.5rem', minWidth: '80px' }}
                    >
                      {numberOfBirds}
                    </div>
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-lg"
                      onClick={handleIncrement}
                      disabled={numberOfBirds >= maxBirds || submitting}
                      style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}
                    >
                      +
                    </button>
                  </div>
                )}
                <small className="text-muted">
                  {isRotationMode ? (
                    'Rotation birds are assigned by the administrator.'
                  ) : maxBirds >= 999 ? (
                    'No limit for admin rotation'
                  ) : (
                    <div>
                      <div className="mb-1">
                        You can request up to <strong>{maxBirds}</strong> {maxBirds === 1 ? 'bird' : 'birds'}
                      </div>
                      {approvedBirdsWithHandler > 0 && (
                        <div className="text-muted small">
                          • {approvedBirdsWithHandler} {approvedBirdsWithHandler === 1 ? 'bird' : 'birds'} already with this handler (5 max per handler)
                        </div>
                      )}
                    </div>
                  )}
                </small>
              </div>

              {!isRotationMode && (
                <div className="mb-3">
                  <label htmlFor="requestNotes" className="form-label fw-bold">
                    Additional Notes <span className="text-muted">(Optional)</span>
                  </label>
                  <textarea
                    id="requestNotes"
                    className="form-control"
                    rows="3"
                    placeholder="Any special instructions or notes for the handler..."
                    value={requestNote}
                    onChange={(e) => setRequestNote(e.target.value)}
                    disabled={submitting}
                    maxLength={500}
                  />
                  <small className="text-muted">
                    {requestNote.length}/500 characters
                  </small>
                </div>
              )}

               {/* Bird Information Section - Shown in Rotation Mode */}
               {isRotationMode && (
                 <div className="p-3 border rounded" style={{ backgroundColor: '#f8f9fa' }}>
                   <h6 className="fw-bold mb-3">Bird Information</h6>
                   <div className="text-muted small mb-3">
                     Enter the details for each bird being reserved. Please provide band numbers, color, and sex information.
                   </div>
                   
                   {birdDetails.map((bird, idx) => (
                     <div key={idx} className="mb-4 pb-3 border-bottom">
                       <div className="d-flex align-items-center mb-3">
                         <span className="badge bg-primary me-2" style={{ fontSize: '0.9rem' }}>Bird {idx + 1}</span>
                       </div>
                       
                       <div className="row g-2">
                         {/* Band Number */}
                         <div className="col-md-6">
                           <label className="form-label small mb-1">Band Number</label>
                           <input
                             type="text"
                             className="form-control form-control-sm"
                             placeholder="Band number"
                             value={bird.bandNumber || ''}
                             onChange={(e) => handleBirdDetailChange(idx, 'bandNumber', e.target.value)}
                             disabled={submitting}
                           />
                         </div>
                         
                          {/* Color */}
                          <div className="col-md-6">
                            <label className="form-label small mb-1">Color</label>
                            <input
                              type="text"
                              className="form-control form-control-sm"
                              placeholder="BB, BC, SPL, GRIZ, etc."
                              value={bird.color}
                              onChange={(e) => handleBirdDetailChange(idx, 'color', e.target.value)}
                              disabled={submitting}
                            />
                          </div>
                         
                         {/* Sex */}
                         <div className="col-md-6">
                           <label className="form-label small mb-1">Sex</label>
                           <select
                             className="form-select form-select-sm"
                             value={bird.sex}
                             onChange={(e) => handleBirdDetailChange(idx, 'sex', e.target.value)}
                             disabled={submitting}
                           >
                             <option value="">Select sex</option>
                             <option value="Cock">Cock</option>
                             <option value="Hen">Hen</option>
                           </select>
                         </div>
                       </div>
                     </div>
                   ))}
                </div>
              )}
            </div>
            
            <div className="modal-footer d-flex flex-column flex-sm-row gap-2 p-3" style={{ 
              flexShrink: 0, 
              borderTop: '1px solid #dee2e6',
              backgroundColor: 'white'
            }}>
              <button 
                type="button" 
                className="btn btn-secondary w-100 w-sm-auto" 
                onClick={handleClose}
                disabled={submitting}
                style={{ minHeight: '48px' }}
              >
                Cancel
              </button>
               <button 
                 type="submit" 
                 className="btn btn-primary w-100 w-sm-auto"
                 disabled={submitting}
                 style={{ minHeight: '48px' }}
               >
                 {submitting ? (isRotationMode ? 'Saving...' : 'Sending Request...') : (isRotationMode ? 'Save' : 'Send Request')}
               </button>
            </div>
          </form>
        </div>
      </div>

      {/* Call Confirmation Modal */}
      {showConfirmation && (
        <div 
          className="modal fade show" 
          style={{ 
            display: 'block', 
            backgroundColor: 'rgba(0,0,0,0.7)',
            zIndex: 1060
          }}
          onClick={handleCancelConfirmation}
        >
          <div 
            className="modal-dialog modal-dialog-centered" 
            style={{ maxWidth: '500px' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="modal-content">
              <div className="modal-header" style={{ borderBottom: '3px solid #0b3d91' }}>
                <h5 className="modal-title" style={{ color: '#0b3d91' }}>
                  <i className="bi bi-telephone-fill me-2"></i>
                  Call Required for Approval
                </h5>
              </div>
              
              <div className="modal-body text-center p-4">
                <div className="mb-4">
                  <i className="bi bi-info-circle-fill text-primary" style={{ fontSize: '3rem' }}></i>
                </div>
                <h6 className="mb-3">I understand I will need to call this handler so they can approve my request.</h6>
                <div className="alert alert-info mb-4">
                  <strong>Handler Phone:</strong> {handler.mobile}
                </div>
                <p className="text-muted">
                  You can submit your request now, but the handler will need you to call them before they approve it.
                </p>
              </div>
              
              <div className="modal-footer d-flex gap-2">
                <button 
                  type="button" 
                  className="btn btn-secondary flex-fill" 
                  onClick={handleCancelConfirmation}
                  style={{ minHeight: '48px' }}
                >
                  Cancel - I'll Find Another Handler
                </button>
                <button 
                  type="button" 
                  className="btn btn-primary flex-fill"
                  onClick={handleConfirmSubmit}
                  disabled={submitting}
                  style={{ minHeight: '48px' }}
                >
                  OK - Send Request
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BreederRequestModal;
